import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit'
import { ActiveClaimCheckStatus } from '@typesApp/ClaimRecord'
import { EligibleProductStatus } from '@typesApp/Product'
import { authApi } from '@redux/queries/auth'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { AppState } from '@redux/store'
import { isClosedOrDeletedStatus } from '@utils/claimUtil'
import { connectorSlice } from './connectorSlice'

export type ClaimStatusState = {
  activeClaimCheckStatus: ActiveClaimCheckStatus
  eligibleProductStatus?: EligibleProductStatus
  isOrderComplete: boolean
  claimRecordDeleted?: boolean
  claimRecordId?: string
  isEligibleProductCheckInProgress?: boolean
}

const initialState: ClaimStatusState = {
  activeClaimCheckStatus: ActiveClaimCheckStatus.NONE,
  isOrderComplete: false,
}

export const claimStatusSlice = createSlice({
  name: 'claimStatus',
  initialState: initialState,
  reducers: {
    setActiveClaimRecord(state, action: PayloadAction<string | undefined>) {
      state.claimRecordId = action.payload
      state.activeClaimCheckStatus = ActiveClaimCheckStatus.COMPLETE
    },
    setClaimRecordDeleted(state) {
      state.claimRecordDeleted = true
      state.claimRecordId = undefined
    },
    setEligibleProductCheckInProgress(state, action: PayloadAction<boolean>) {
      state.isEligibleProductCheckInProgress = action.payload
    },
    setEligibleProductCheckResult(state, action: PayloadAction<EligibleProductStatus>) {
      state.eligibleProductStatus = action.payload
    },
    setEligibleProductCheckError(state) {
      // reset PENDING state on error
      if (state.eligibleProductStatus === EligibleProductStatus.PENDING) {
        state.eligibleProductStatus = EligibleProductStatus.NA
      }
    },
    resetClaimStatusInitialState() {
      return initialState
    },
  },
  extraReducers: builder => {
    /**
     * Order complete notification
     */
    builder.addCase(connectorSlice.actions.connectorOrderComplete, state => {
      state.isOrderComplete = true
    })

    /**
     * Active claim check status
     */
    builder.addMatcher(
      isAnyOf(authApi.endpoints.getAuthToken.matchPending, claimRecordsApi.endpoints.getActiveClaimRecord.matchPending),
      state => {
        if (state.activeClaimCheckStatus === ActiveClaimCheckStatus.NONE) {
          state.activeClaimCheckStatus = ActiveClaimCheckStatus.PENDING
        }
      }
    )

    builder.addMatcher(
      isAnyOf(
        authApi.endpoints.getAuthToken.matchRejected,
        claimRecordsApi.endpoints.getActiveClaimRecord.matchRejected
      ),
      state => {
        state.activeClaimCheckStatus = ActiveClaimCheckStatus.COMPLETE
      }
    )

    /**
     * On create claim record clear the order complete flag so next fetch will update claim details
     */
    builder.addMatcher(claimRecordsApi.endpoints.createClaimRecord.matchFulfilled, (state, action) => {
      state.isOrderComplete = false
      state.claimRecordId = action.payload.id
      state.claimRecordDeleted = undefined
    })

    builder.addMatcher(claimRecordsApi.endpoints.getClaimRecord.matchFulfilled, (state, action) => {
      if (state.claimRecordDeleted && !isClosedOrDeletedStatus(action.payload?.status)) {
        state.claimRecordDeleted = undefined
      }
    })

    builder.addMatcher(claimRecordsApi.endpoints.getClaimRecord.matchRejected, (state, action) => {
      const status = action.payload?.status
      if (status === 404) {
        state.claimRecordDeleted = true
      }
    })

    /**
     * Remove claim details after claim record deleted
     */
    builder.addMatcher(claimRecordsApi.endpoints.deleteClaimRecord.matchFulfilled, state => {
      state.claimRecordDeleted = true
      state.claimRecordId = undefined
    })

    /**
     * Reset to initial state on session expiry or logout
     */
    builder.addMatcher(
      isAnyOf(connectorSlice.actions.sessionExpired, connectorSlice.actions.connectorLogoutInProgress),
      () => {
        return initialState
      }
    )
  },
})

export const {
  setActiveClaimRecord,
  setClaimRecordDeleted,
  setEligibleProductCheckInProgress,
  setEligibleProductCheckResult,
  setEligibleProductCheckError,
  resetClaimStatusInitialState,
} = claimStatusSlice.actions

export const claimRecordIdSelector = (state: AppState): string => state.claimStatus.claimRecordId || ''
export const claimRecordDeletedSelector = (state: AppState): boolean => state.claimStatus.claimRecordDeleted === true
export const eligibleProductCheckInProgressSelector = (state: AppState): boolean | undefined =>
  state.claimStatus.isEligibleProductCheckInProgress
export const activeClaimCheckStatusSelector = (state: AppState): ActiveClaimCheckStatus =>
  state.claimStatus.activeClaimCheckStatus
